// Compression vidéo avant envoi — NATIF UNIQUEMENT (Android/iOS).
//
// Une vidéo brute de téléphone (4K, 400-600 Mo pour 5 min) mettrait ~20 min à partir :
// sur l'app native, elle est compressée matériellement par CHEMIN de fichier via le plugin
// `capacitor-native-video-compressor` (Android LightCompressor/MediaCodec, iOS AVFoundation)
// avant l'envoi en arrière-plan.
//
// Sur le web/PWA, la vidéo est envoyée TELLE QUELLE : l'ancien ré-encodage temps réel dans le
// navigateur (canvas.captureStream + MediaRecorder) produisait un WebM SANS durée ni index
// (durée « inconnue » → mal classé en Reel, lecteur qui saute, trous si l'onglet passait en
// arrière-plan). Le HLS adaptatif est fabriqué côté serveur (Bunny) ; la limite de taille admin
// (Mo) reste le garde-fou.
//
// Politique de qualité (choix user) : Fil & Reels (cultes, prédications, clips) → ~1080p QUEL QUE SOIT le réseau
// du créateur (netteté TikTok — Bunny ne fabrique jamais mieux que la source) ; Stories / Chat → ~720p.
// L'admin peut PLAFONNER l'ensemble (Paramètres › Limites › « Compression vidéo — plafond ») : 1080p (défaut) /
// 720p / 540p — utile pour économiser la data des créateurs à grande échelle.
// Le spectateur n'est pas concerné : Bunny refabrique toujours l'échelle 240p→1080p et le lecteur
// choisit la variante que SON réseau mesuré permet.
import { Capacitor } from "@capacitor/core";

// Presets du plugin : VERY_HIGH ≈ 1080p · HIGH ≈ 720p · MEDIUM ≈ 540p (MEDIUM rendait les Reels flous face à TikTok).
const PRESET_RANK = ["MEDIUM", "HIGH", "VERY_HIGH"];
const CAP_PRESET = { "1080p": "VERY_HIGH", "720p": "HIGH", "540p": "MEDIUM" };
export const nativeQualityFor = (context) => (context === "feed" ? "VERY_HIGH" : "HIGH");
// Plafond admin (settings.video_compression_cap) appliqué par-dessus la politique : min(politique, plafond).
export const nativeQualityCapped = (context, config) => {
  const policy = nativeQualityFor(context);
  const cap = CAP_PRESET[config?.settings?.video_compression_cap] || "VERY_HIGH";
  return PRESET_RANK[Math.min(PRESET_RANK.indexOf(policy), PRESET_RANK.indexOf(cap))];
};

// Android (LightCompressor) : la résolution de sortie ne dépendait PAS du preset — ÷2 dès qu'un côté ≥ 1920 px
// (un 1080p de téléphone devenait 540p → Bunny plafonnait à 480p), ×0,75 dès 1280 px. On impose donc, comme iOS,
// un plafond par preset (plus grand côté) + un débit borné (jamais au-dessus de la source, jamais d'upscale).
// iOS ignore ces champs (ses presets sont déjà 1280×720 / 1920×1080). Bunny refabrique l'échelle jusqu'à 1080p.
export const NATIVE_PRESET_CAPS = {
  VERY_HIGH: { maxLongSide: 1920, maxBitrateMbps: 6 },
  HIGH: { maxLongSide: 1280, maxBitrateMbps: 4 },
  MEDIUM: { maxLongSide: 960, maxBitrateMbps: 2 },
};

// NATIF : compression matérielle par chemin. Renvoie un NOUVEAU chemin (compressé),
// ou le chemin d'ORIGINE en cas d'échec — l'envoi ne casse jamais.
// Accès via registerPlugin (pas d'import du paquet) pour ne jamais embarquer l'implémentation
// web FFmpeg.wasm du plugin dans le build web.
export async function compressVideoPathNative(path, { context = "feed", config, onProgress, signal } = {}) {
  if (!Capacitor.isNativePlatform() || !path) return path;
  let listener = null;
  try {
    const { registerPlugin } = await import("@capacitor/core");
    const NVC = registerPlugin("NativeVideoCompressor");
    if (!NVC || typeof NVC.compressVideo !== "function") return path;
    const jobId = `yv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    if (signal) {
      signal.addEventListener("abort", () => { try { NVC.cancel({ jobId }); } catch { /* noop */ } }, { once: true });
    }
    try {
      listener = await NVC.addListener("onProgress", (info) => {
        if (info?.status === "progress" && typeof info.percent === "number") {
          onProgress?.(Math.max(0, Math.min(100, Math.round(info.percent))));
        }
      });
    } catch { /* progression optionnelle */ }
    const quality = nativeQualityCapped(context, config);
    const res = await NVC.compressVideo({ sourcePath: path, quality, jobId, ...(NATIVE_PRESET_CAPS[quality] || {}) });
    return res?.success && res?.destPath ? res.destPath : path;
  } catch (e) {
    // Lot 736 — le fichier choisi n'est plus lisible (accès au sélecteur photo expiré) : envoyer « l'original » échouerait
    // aussi → on remonte une erreur claire, le composeur demande de re-choisir la vidéo au lieu de planter / d'échouer en silence.
    if (e?.code === "SOURCE_UNREADABLE") throw Object.assign(new Error("source_unreadable"), { code: "SOURCE_UNREADABLE" });
    return path; // plugin absent / annulé / échec natif / audio non copiable (AUDIO_UNSAFE) → on envoie l'original
  } finally {
    try { await listener?.remove?.(); } catch { /* noop */ }
  }
}
